import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { BookOpenIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, PlayCircleIcon, StarIcon, UsersIcon, ClockIcon, AwardIcon, MessageCircleIcon, TrendingUpIcon, FileTextIcon, SearchIcon, BriefcaseIcon, CompassIcon, ZapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { ContactForm } from '../../components/ContactForm';
import { FaqSection } from '../../components/FaqSection';
import { useSEO } from '../../hooks/useSEO';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslations } from '../../i18n/translations';
function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative pt-20 lg:pt-24 pb-20 overflow-hidden bg-gradient-to-br from-amber-900 via-amber-800 to-navy-900" data-id="element-885" data-mp="36s3ge0ewv0yk">
      <div className="absolute inset-0" data-id="element-886" data-mp="19gvnxels8ojd">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-amber-500/10 blur-3xl hidden sm:block" data-id="element-887" data-mp="19g3uoafnr2w9" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-orange-500/8 blur-3xl hidden sm:block" data-id="element-888" data-mp="1dfl1p8aqg83j" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-889" data-mp="1n8a8hpvc8xsj" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-890" data-mp="ukwkwidj5el3">
        <motion.nav initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex items-center gap-1.5 text-sm text-amber-300/70 mb-10" data-id="element-891" data-mp="zgv29m0fxwc0">
          <Link to="/" className="hover:text-amber-300 transition-colors" data-id="element-892" data-mp="1q8agtnzjvmit">
            {t.nav.home}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-893" data-mp="1l9penj0qmsrq" />
          <Link to="/#services" className="hover:text-amber-300 transition-colors" data-id="element-894" data-mp="1l8xvj7obunic">
            {t.nav.services}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-895" data-mp="1v8eyvig3wpb5" />
          <span className="text-amber-200" data-id="element-896" data-mp="1v5j2hyjwq8ot">{t.careerConsulting.title}</span>
        </motion.nav>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-897" data-mp="17fg99f49awom">
          <div data-id="element-898" data-mp="12kj5amlu3dil">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.05
          }} className="flex items-center gap-3 mb-6" data-id="element-899" data-mp="12gv9fgsx2f0p">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-xl shadow-amber-500/30" data-id="element-900" data-mp="jryfje1floj0">
                <BookOpenIcon className="w-6 h-6" data-id="element-901" data-mp="30720goxfroak" />
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-semibold rounded-full border border-amber-500/20" data-id="element-902" data-mp="2li5ls90vjd0z">
                {t.careerConsulting.badge}
              </span>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1
          }} data-id="element-903" data-mp="3e3yvl2kl7rx9">
              <p className="text-amber-300/80 text-base font-medium mb-2" data-id="element-904" data-mp="1tqgd32he83ac">
                {t.careerConsulting.author}
              </p>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-[1.0]" data-id="element-905" data-mp="1xsl19lp2o7pl">
                {t.careerConsulting.h1}
              </h1>
              <p className="mt-3 text-xl text-amber-200/80 font-medium" data-id="element-906" data-mp="297y132iyamlv">
                {t.careerConsulting.h1_sub}
              </p>
            </motion.div>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.18
          }} className="mt-5 text-lg text-amber-200/70 leading-relaxed" data-id="element-907" data-mp="246s771dk0tar">
              {t.careerConsulting.subtitle}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.25
          }} className="mt-8 flex flex-wrap gap-4" data-id="element-908" data-mp="2ixhlwdq84qjd">
              <a href="#contact" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-amber-900 bg-white rounded-xl hover:bg-amber-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-909" data-mp="3n8uh39yo5z3y">
                {t.careerConsulting.cta_primary}
                <ArrowRightIcon className="w-4 h-4" data-id="element-910" data-mp="5qjecgoonuzw" />
              </a>
              <a href="#pricing" className="inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 transition-all" data-id="element-911" data-mp="apw8lmhvw4ci">
                {t.careerConsulting.cta_secondary}
              </a>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="grid grid-cols-2 gap-4" data-id="element-912" data-mp="2gl70xoztdyls">
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-913" data-mp="1p6k8jktxmae">
              <div className="text-4xl font-extrabold text-white" data-id="element-914" data-mp="3sv7y0zw6itzq">
                {t.careerConsulting.stats.lessons_val}
              </div>
              <div className="text-sm text-amber-300/70 mt-1" data-id="element-915" data-mp="bl9wwjdb53bl">
                {t.careerConsulting.stats.lessons_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-916" data-mp="kewmfrqx9acc">
              <div className="text-4xl font-extrabold text-amber-400" data-id="element-917" data-mp="gdtavhfwq0gx">
                {t.careerConsulting.stats.time_val}
              </div>
              <div className="text-sm text-amber-300/70 mt-1" data-id="element-918" data-mp="ue42wnsydl3o">
                {t.careerConsulting.stats.time_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-919" data-mp="190au2un9ptls">
              <div className="text-4xl font-extrabold text-white" data-id="element-920" data-mp="1dz47kn2k1kly">
                {t.careerConsulting.stats.exp_val}
              </div>
              <div className="text-sm text-amber-300/70 mt-1" data-id="element-921" data-mp="z0trwn785xuk">
                {t.careerConsulting.stats.exp_label}
              </div>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-922" data-mp="142re9qax98rq">
              <div className="text-4xl font-extrabold text-amber-400" data-id="element-923" data-mp="q7p3opv4qv8l">
                {t.careerConsulting.stats.price_val}
              </div>
              <div className="text-sm text-amber-300/70 mt-1" data-id="element-924" data-mp="gc5awcbv4co2">
                {t.careerConsulting.stats.price_label}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function WhatYouGetSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const items = [{
    icon: PlayCircleIcon,
    text: t.careerConsulting.whatYouGet.items[0]
  }, {
    icon: FileTextIcon,
    text: t.careerConsulting.whatYouGet.items[1]
  }, {
    icon: BookOpenIcon,
    text: t.careerConsulting.whatYouGet.items[2]
  }, {
    icon: SearchIcon,
    text: t.careerConsulting.whatYouGet.items[3]
  }, {
    icon: BookOpenIcon,
    text: t.careerConsulting.whatYouGet.items[4]
  }, {
    icon: MessageCircleIcon,
    text: t.careerConsulting.whatYouGet.items[5]
  }, {
    icon: UsersIcon,
    text: t.careerConsulting.whatYouGet.items[6]
  }];
  return <section className="py-20 lg:py-28 bg-white" ref={ref} data-id="element-925" data-mp="3qagxs3inostz">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-926" data-mp="3la2x56jdwl5z">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-927" data-mp="3l9jbbeqm5cmo">
          <span className="text-sm font-semibold text-amber-600 tracking-wide uppercase" data-id="element-928" data-mp="25sogpo6i8b84">
            {t.careerConsulting.whatYouGet.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-929" data-mp="1d2ze56e5c34e">
            {t.careerConsulting.whatYouGet.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-930" data-mp="1hzpvry5ln6rx">
            {t.careerConsulting.whatYouGet.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-931" data-mp="1rsnea4alcvqj">
          {items.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: i * 0.07
        }} className="flex items-start gap-4 p-5 rounded-2xl bg-amber-50/60 border border-amber-100" data-id="element-932" data-mp="ubon8p8f7qci" data-mpi={i}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-500/20" data-id="element-933" data-mp="3tsvt62crik9v">
                <item.icon className="w-4 h-4" data-id="element-934" data-mp="7l5wmhu1epcs" />
              </div>
              <p className="text-sm text-navy-700 leading-relaxed pt-1.5" data-id="element-935" data-mp="7itbjvszfyvi">
                {item.text}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="mt-10 p-6 rounded-2xl bg-navy-900 text-center max-w-2xl mx-auto" data-id="element-936" data-mp="3b13c96aywpii">
          <p className="text-white font-semibold mb-1" data-id="element-937" data-mp="1j5sizw42k1u3">
            {t.careerConsulting.whatYouGet.free_lesson_title}
          </p>
          <p className="text-navy-400 text-sm" data-id="element-938" data-mp="1e6z88bg62oxm">
            {t.careerConsulting.whatYouGet.free_lesson_desc}
          </p>
        </motion.div>
      </div>
    </section>;
}
function ModulesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const modules = t.careerConsulting.modules.items.map((mod, i) => ({
    ...mod,
    highlight: i === 5
  }));
  return <section className="py-20 lg:py-28 bg-navy-50/40" ref={ref} data-id="element-939" data-mp="qifwflxwru0s">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-940" data-mp="gtskoic124c7">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-941" data-mp="gvlprt6zsgi3">
          <span className="text-sm font-semibold text-amber-600 tracking-wide uppercase" data-id="element-942" data-mp="zlbmilubfccn">
            {t.careerConsulting.modules.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-943" data-mp="qrygci78qe8m">
            {t.careerConsulting.modules.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-944" data-mp="ur7a3n2a6c46">
            {t.careerConsulting.modules.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-945" data-mp="1nammw27r74q9">
          {modules.map((mod, i) => <motion.div key={mod.num} initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className={`p-7 rounded-2xl border ${mod.highlight ? 'bg-gradient-to-br from-amber-500 to-amber-600 border-amber-400' : 'bg-white border-navy-100'}`} data-id="element-946" data-mp="37yylac7hhh1k" data-mpi={i}>
              <div className={`text-5xl font-extrabold mb-4 leading-none ${mod.highlight ? 'text-white/30' : 'text-navy-100'}`} data-id="element-947" data-mp="2z0mwmfq6gtbr">
                {mod.num}
              </div>
              <h3 className={`text-base font-bold mb-4 leading-snug ${mod.highlight ? 'text-white' : 'text-navy-900'}`} data-id="element-948" data-mp="eb77rqaireor">
                {mod.title}
              </h3>
              <ul className="space-y-2" data-id="element-949" data-mp="2f0d6hhj61azo">
                {mod.topics.map((topic, j) => <li key={j} className="flex items-start gap-2" data-id="element-950" data-mp="2jxn2jg128kwh" data-mpi={j}>
                    <CheckCircleIcon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${mod.highlight ? 'text-white/70' : 'text-amber-500'}`} data-id="element-951" data-mp="2nxx6kn5al18p" />
                    <span className={`text-xs leading-relaxed ${mod.highlight ? 'text-amber-100' : 'text-navy-500'}`} data-id="element-952" data-mp="2xxo0amm13c7q">
                      {topic}
                    </span>
                  </li>)}
              </ul>
              {mod.extra && <div className={`mt-4 pt-4 border-t text-xs font-medium ${mod.highlight ? 'border-white/20 text-amber-200' : 'border-navy-100 text-amber-600'}`} data-id="element-953" data-mp="3bosydk9qzwfy">
                  📎 {mod.extra}
                </div>}
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function PricingSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const plans = t.careerConsulting.pricing.plans.map((plan, i) => ({
    ...plan,
    highlight: i === 1
  }));
  return <section className="py-20 lg:py-28 bg-white" id="pricing" ref={ref} data-id="element-954" data-mp="icakfbtzias3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-955" data-mp="idlsvoydk6pj">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-956" data-mp="dd7qamfjloyw">
          <span className="text-sm font-semibold text-amber-600 tracking-wide uppercase" data-id="element-957" data-mp="wbadcvoj5bu2">
            {t.careerConsulting.pricing.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-958" data-mp="2qlqlrq8w7579">
            {t.careerConsulting.pricing.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-xl mx-auto" data-id="element-959" data-mp="2gt1j9kvbl1o2">
            {t.careerConsulting.pricing.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start" data-id="element-960" data-mp="27sd60sdr35c8">
          {plans.map((plan, i) => <motion.div key={plan.name} initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className={`relative rounded-2xl border overflow-hidden ${plan.highlight ? 'bg-gradient-to-b from-amber-500 to-amber-600 border-amber-400 shadow-2xl shadow-amber-500/25' : 'bg-white border-navy-100 shadow-sm'}`} data-id="element-961" data-mp="2ct0wncy2hk5x" data-mpi={i}>
              {plan.highlight && <div className="absolute top-0 left-0 right-0 h-1 bg-white/30" data-id="element-962" data-mp="1vbvgt57kjep3" />}
              {plan.highlight && <div className="text-center py-2 bg-white/15" data-id="element-963" data-mp="24ayyc4r37m1h">
                  <span className="text-xs font-bold text-white uppercase tracking-widest" data-id="element-964" data-mp="249e30cwy7z2k">
                    {t.careerConsulting.pricing.popular_badge}
                  </span>
                </div>}
              <div className="p-8" data-id="element-965" data-mp="2e7291z008t01">
                <h3 className={`text-lg font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-navy-900'}`} data-id="element-966" data-mp="299jzq7i2sbri">
                  «{plan.name}»
                </h3>
                <div className={`text-4xl font-extrabold mb-6 ${plan.highlight ? 'text-white' : 'text-navy-900'}`} data-id="element-967" data-mp="ewkd3ufa2haq">
                  {plan.price}
                </div>
                <ul className="space-y-3 mb-8" data-id="element-968" data-mp="3u6u6yqdlwb7a">
                  {plan.features.map((f, j) => <li key={j} className="flex items-start gap-2.5" data-id="element-969" data-mp="2s50xdid39iq" data-mpi={j}>
                      <CheckCircleIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlight ? 'text-white/80' : 'text-amber-500'}`} data-id="element-970" data-mp="2v10155y448r" />
                      <span className={`text-sm leading-relaxed ${plan.highlight ? 'text-amber-100' : 'text-navy-600'}`} data-id="element-971" data-mp="36bhtoz9eo8n8">
                        {f}
                      </span>
                    </li>)}
                </ul>
                <a href="#contact" className={`block text-center px-6 py-3.5 rounded-xl font-semibold text-sm transition-all ${plan.highlight ? 'bg-white text-amber-700 hover:bg-amber-50 shadow-lg' : 'bg-amber-500 text-white hover:bg-amber-600 shadow-md shadow-amber-500/20'}`} data-id="element-972" data-mp="3l7z4npdonl99">
                  {plan.cta}
                </a>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
function CommunitiesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-16 bg-navy-50/40" ref={ref} data-id="element-973" data-mp="218duk0f24bun">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-974" data-mp="1x7tvuseu506e">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-10" data-id="element-975" data-mp="1s9sbm45oautc">
          <span className="text-sm font-semibold text-amber-600 tracking-wide uppercase" data-id="element-976" data-mp="3hmngyozqnqiu">
            {t.careerConsulting.communities.eyebrow}
          </span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-navy-900 tracking-tight" data-id="element-977" data-mp="8fdgmu1x5j11">
            {t.careerConsulting.communities.title}
          </h2>
          <p className="mt-3 text-navy-500" data-id="element-978" data-mp="3utww6u2mr493">
            {t.careerConsulting.communities.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-979" data-mp="3qtcureb0xeh5">
          {[{
          name: t.careerConsulting.communities.items[0].name,
          desc: t.careerConsulting.communities.items[0].desc,
          link: 'https://t.me/sergeydenisyuk',
          color: 'from-navy-700 to-navy-900'
        }, {
          name: t.careerConsulting.communities.items[1].name,
          desc: t.careerConsulting.communities.items[1].desc,
          link: 'https://t.me/sergeydenisyuk',
          color: 'from-amber-600 to-amber-800'
        }, {
          name: t.careerConsulting.communities.items[2].name,
          desc: t.careerConsulting.communities.items[2].desc,
          link: 'https://t.me/sergeydenisyuk',
          color: 'from-violet-700 to-violet-900'
        }].map((community, i) => <motion.a key={community.name} href={community.link} target="_blank" rel="noopener noreferrer" initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className={`p-6 rounded-2xl bg-gradient-to-br ${community.color} text-white text-center hover:-translate-y-1 transition-transform`} data-id="element-980" data-mp="1wbbinogm9hhh" data-mpi={i}>
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center mx-auto mb-3" data-id="element-981" data-mp="1zeg0mzh2bu5t">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-982" data-mp="29asqtndefpvt">
                  <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-983" data-mp="24d0y4r4xjzqk" />
                </svg>
              </div>
              <div className="font-bold text-sm" data-id="element-984" data-mp="34jmp0g4qpg22">{community.name}</div>
              <div className="text-xs text-white/70 mt-1" data-id="element-985" data-mp="39acr9khvd2sj">{community.desc}</div>
            </motion.a>)}
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-986" data-mp="3n8kuub9ev2co">
      <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-amber-900 via-amber-800 to-navy-900 relative overflow-hidden" data-id="element-987" data-mp="22uaqvt6c2wd7">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-amber-500/15 blur-3xl hidden sm:block" data-id="element-988" data-mp="26xgwcylmgjim" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-orange-400/10 blur-3xl hidden sm:block" data-id="element-989" data-mp="2n5dchk5ko7ne" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-990" data-mp="2ddh0d56nih06">
          <div className="flex-1" data-id="element-991" data-mp="28ee5ueoart6g">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-992" data-mp="28f5s2yw08ek2">
              {t.careerConsulting.ctaBanner.title}
            </h3>
            <p className="mt-2 text-amber-200/80 leading-relaxed" data-id="element-993" data-mp="35zsb38nz1yap">
              {t.careerConsulting.ctaBanner.subtitle}
            </p>
          </div>
          <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-amber-900 bg-white rounded-xl hover:bg-amber-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-994" data-mp="2w2nml26nlqqq">
            {t.careerConsulting.ctaBanner.button}
            <ArrowRightIcon className="w-4 h-4" data-id="element-995" data-mp="1gmezcydj57zk" />
          </a>
        </div>
      </div>
    </motion.div>;
}
const seoData = {
  UA: {
    title: "Курс «Кар'єра» — Онлайн-курс Сергія Денисюка | ESCA",
    description: "Авторський онлайн-курс «Кар'єра» від CEO ESCA Сергія Денисюка. 5 відео-уроків, побудова кар'єрної стратегії, резюме що продає. Від $300.",
    keywords: "курс кар'єра, кар'єрний консалтинг, резюме, пошук роботи, Сергій Денисюк, ESCA"
  },
  RU: {
    title: 'Курс «Карьера» — Онлайн-курс Сергея Денисюка | ESCA',
    description: 'Авторский онлайн-курс «Карьера» от CEO ESCA Сергея Денисюка. 5 видео-уроков, построение карьерной стратегии, продающее резюме. От $300.',
    keywords: 'курс карьера, карьерный консалтинг, резюме, поиск работы, Сергей Денисюк, ESCA'
  },
  EN: {
    title: 'Career Course — Online Course by Sergiy Denysyuk | ESCA',
    description: "Author's online career course by ESCA CEO Sergiy Denysyuk. 5 video lessons, career strategy, selling resume. From $300.",
    keywords: 'career course, career consulting, resume, job search, Sergiy Denysyuk, ESCA'
  }
};
export function CareerConsultingPage() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const seo = seoData[lang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-996" data-mp="5t4xjl0a762g">
      <Header data-id="element-997" data-mp="kqnjkflkr567" />
      <main className="pt-16 lg:pt-20" data-id="element-998" data-mp="kqdwazrwzc47">
        <HeroSection data-id="element-999" data-mp="kp2p0pz9fm8c" />
        <WhatYouGetSection data-id="element-1000" data-mp="fs3vo9nf7jb7" />
        <ModulesSection data-id="element-1001" data-mp="fsvrnlkxjj20" />
        <PricingSection data-id="element-1002" data-mp="frkpfq74ujd0" />
        <CommunitiesSection data-id="element-1003" data-mp="fpzsjtxciobz" />
        <FaqSection items={t.careerConsulting.faqItems} accent="amber" data-id="element-1004" data-mp="tk0lg71m9h4f" />
        <CtaBanner data-id="element-1005" data-mp="tlbna1bi4esw" />
        <div id="contact" data-id="element-1006" data-mp="pi5o1dmt3nvx">
          <ContactForm data-id="element-1007" data-mp="piyltp3sf31x" />
        </div>
      </main>
      <Footer data-id="element-1008" data-mp="pk9plox8gat1" />
    </div>;
}